import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Package, Factory, AlertTriangle, Clock, ArrowDown, ArrowUp, Settings2, AlertOctagon, ShoppingCart, FileSignature, Wrench, Building2 } from "lucide-react";
import { fmtDate, fmtDateTime, fmtNum, movementLabel } from "../lib/format";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import DlcComplianceBanner from "../components/DlcComplianceBanner";

const KpiCard = ({ label, value, hint, icon: Icon, tone = "default", testid, to }) => {
  const toneMap = {
    default: "bg-white",
    warning: "bg-[#FBF4E9] border-[#E8D3A8]",
    danger: "bg-[#FCEEEA] border-[#E8C1B4]",
  };
  const inner = (
    <div data-testid={testid} className={`border border-[#E5E5E0] rounded-lg p-6 ${toneMap[tone]} ${to ? "transition hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57]">{label}</div>
          <div className="font-heading text-4xl font-medium text-[#1A1C1A] mt-3">{value}</div>
          {hint && <div className="text-sm text-[#575A57] mt-1">{hint}</div>}
        </div>
        <div className="w-10 h-10 rounded-md bg-[#F0F0EB] flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#1F4E3D]" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
};

const MovementIcon = ({ type }) => {
  if (type === "IN") return <ArrowDown className="w-4 h-4 text-[#2D6A4F]" />;
  if (type === "OUT") return <ArrowUp className="w-4 h-4 text-[#C05C47]" />;
  return <Settings2 className="w-4 h-4 text-[#D4A373]" />;
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [velocity, setVelocity] = useState({ totals: {} });
  const [losses, setLosses] = useState({ totals: {} });
  const [overdue, setOverdue] = useState([]);
  const [productionNeeds, setProductionNeeds] = useState({ items: [] });
  const [pendingResellerApps, setPendingResellerApps] = useState(0);
  // Phase 101ew — Hôtes distributeur en attente + revendeurs invités mais non activés
  const [pendingHostApps, setPendingHostApps] = useState(0);
  const [pendingActivations, setPendingActivations] = useState(0);

  useEffect(() => {
    const safe = (p, fallback) => p.then((r) => r.data).catch(() => fallback);
    Promise.all([
      safe(api.get("/dashboard/stats"), null),
      safe(api.get("/products", { params: { include_archived: true } }), []),
      safe(api.get("/distributors"), []),
      safe(api.get("/sales/velocity"), { totals: {} }),
      safe(api.get("/expired-stock/stats"), { totals: {} }),
      safe(api.get("/invoices/overdue/list"), []),
      safe(api.get("/production-needs"), { items: [] }),
      safe(api.get("/admin/reseller-applications", { params: { status: "pending", limit: 1 } }), null),
      // Phase 101ew — Hôtes + activations en retard
      safe(api.get("/admin/distributor-host-applications", { params: { status: "pending", limit: 1 } }), null),
      safe(api.get("/resellers"), []),
    ]).then(([s, p, d, v, x, ov, pn, ra, ha, rl]) => {
      if (s) setStats(s);
      setProducts(p);
      setDistributors(d);
      setVelocity(v);
      setLosses(x);
      setOverdue(ov);
      setProductionNeeds(pn);
      setPendingResellerApps(ra?.stats?.pending || 0);
      setPendingHostApps(ha?.stats?.pending || 0);
      // Compte les revendeurs ayant `activation_token` mais pas `activated_at`
      const notActivated = (rl || []).filter((r) => r.activation_token && !r.activated_at);
      setPendingActivations(notActivated.length);
    });
  }, []);

  const productName = (id) => products.find((p) => p.id === id)?.name || "—";
  const productUnit = (id) => products.find((p) => p.id === id)?.unit || "";
  const distributorName = (id) => {
    if (!id) return null;
    const d = distributors.find((x) => x.id === id);
    return d?.name || d?.code || null;
  };

  if (!stats) return <div className="text-[#575A57]">Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Phase 101cg / 101ew — Bannière candidatures pro + activations en attente.
          Regroupé en un seul bloc à trois lignes discrètes pour tout voir en 1 coup d'œil. */}
      {(pendingResellerApps > 0 || pendingHostApps > 0 || pendingActivations > 0) && (
        <div className="border-l-4 border-[#8C6B2F] bg-[#FBF4E9] rounded-r px-4 py-3 space-y-1.5" data-testid="dashboard-pro-banner">
          {pendingResellerApps > 0 && (
            <Link to="/candidatures-pro" className="flex items-center gap-2 text-sm text-[#7A5628] hover:text-[#5A3E1A]" data-testid="dashboard-reseller-apps-banner">
              <AlertOctagon className="w-4 h-4 shrink-0" />
              <span><strong>{pendingResellerApps} candidature{pendingResellerApps > 1 ? "s" : ""} revendeur</strong> en attente d&apos;examen</span>
            </Link>
          )}
          {pendingHostApps > 0 && (
            <Link to="/candidatures-pro" className="flex items-center gap-2 text-sm text-[#5B3A8C] hover:text-[#3F286B]" data-testid="dashboard-host-apps-banner">
              <AlertOctagon className="w-4 h-4 shrink-0" />
              <span><strong>{pendingHostApps} candidature{pendingHostApps > 1 ? "s" : ""} hôte distributeur</strong> à examiner</span>
            </Link>
          )}
          {pendingActivations > 0 && (
            <Link to="/revendeurs" className="flex items-center gap-2 text-sm text-[#8A2E1E] hover:text-[#5F1F14]" data-testid="dashboard-pending-activations-banner">
              <AlertOctagon className="w-4 h-4 shrink-0" />
              <span><strong>{pendingActivations} revendeur{pendingActivations > 1 ? "s" : ""}</strong> avec lien d&apos;activation envoyé mais non cliqué — pensez à relancer</span>
            </Link>
          )}
        </div>
      )}

      {/* Bannière compliance DLC : visible UNIQUEMENT si lots périmés en distrib */}
      <DlcComplianceBanner />

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
        <KpiCard label="Produits référencés" value={stats.total_products} hint={`${stats.total_lots} lots actifs`} icon={Package} testid="kpi-products" to="/produits" />
        <KpiCard label="Ordres de fabrication" value={stats.total_active_orders} hint="Actifs (planifiés + en cours)" icon={Factory} testid="kpi-orders" to="/ordres" />
        <KpiCard label="Alertes stock bas" value={stats.low_stock_count} hint="Sous le seuil minimum" icon={AlertTriangle} tone={stats.low_stock_count > 0 ? "danger" : "default"} testid="kpi-low-stock" />
        <KpiCard
          label="DLC à risque"
          value={stats.dlc_risk_count ?? 0}
          hint={`${stats.dlc_expired_count ?? 0} dépassée${(stats.dlc_expired_count ?? 0) > 1 ? "s" : ""} · ${stats.dlc_soon_count ?? 0} ≤ 7j`}
          icon={AlertOctagon}
          tone={(stats.dlc_risk_count ?? 0) > 0 ? "danger" : "default"}
          testid="kpi-dlc-risk"
          to="/stocks?dlc=risk"
        />
        <KpiCard label="DLC à surveiller" value={stats.expiring_count} hint="Sous 30 jours" icon={Clock} tone={stats.expiring_count > 0 ? "warning" : "default"} testid="kpi-expiring" to="/stocks" />
      </div>

      {/* Ventes & Pertes (intégration Nayax à venir) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <KpiCard
          label="Ventes 24h"
          value={fmtNum(velocity?.totals?.["24h"] || 0)}
          hint="Toutes machines confondues"
          icon={ShoppingCart}
          testid="kpi-sales-24h"
          to="/ventes"
        />
        <KpiCard
          label="Ventes 7 jours"
          value={fmtNum(velocity?.totals?.["7d"] || 0)}
          hint={`${fmtNum(velocity?.totals?.["30d"] || 0)} sur 30j · ${fmtNum(velocity?.totals?.["365d"] || 0)} sur 1 an`}
          icon={ShoppingCart}
          testid="kpi-sales-7d"
          to="/ventes"
        />
        <KpiCard
          label="Pertes DLC 7 jours"
          value={fmtNum(losses?.totals?.["7d"] || 0)}
          hint={`${fmtNum(losses?.totals?.["30d"] || 0)} sur 30j`}
          icon={AlertTriangle}
          tone={(losses?.totals?.["7d"] || 0) > 0 ? "danger" : "default"}
          testid="kpi-losses-7d"
          to="/ventes"
        />
        <KpiCard
          label="Ratio pertes 30j"
          value={(() => {
            const sales30 = velocity?.totals?.["30d"] || 0;
            const loss30 = losses?.totals?.["30d"] || 0;
            const total = sales30 + loss30;
            return total > 0 ? `${((loss30 / total) * 100).toFixed(1)}%` : "—";
          })()}
          hint="Pertes ÷ (ventes + pertes)"
          icon={AlertOctagon}
          tone={(() => {
            const sales30 = velocity?.totals?.["30d"] || 0;
            const loss30 = losses?.totals?.["30d"] || 0;
            const total = sales30 + loss30;
            const ratio = total > 0 ? (loss30 / total) * 100 : 0;
            return ratio > 20 ? "danger" : ratio > 10 ? "warning" : "default";
          })()}
          testid="kpi-loss-ratio"
        />
      </div>

      {/* Factures en retard */}
      {overdue.length > 0 && (
        <Card className="border-[#E8C1B4] bg-[#FCEEEA]" data-testid="dashboard-overdue-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-[#C05C47]" />
              <span className="text-sm font-medium text-[#C05C47]">{overdue.length} facture{overdue.length > 1 ? "s" : ""} en retard de paiement</span>
              <span className="ml-auto font-mono text-sm text-[#C05C47]">
                {overdue.reduce((acc, i) => acc + ((i.total_ttc || 0) - (i.paid_amount || 0)), 0).toFixed(2)} CHF
              </span>
            </div>
            <div className="space-y-1">
              {overdue.slice(0, 3).map((i) => (
                <Link key={i.id} to={`/factures/${i.id}`} className="block text-xs hover:underline text-[#1A1C1A]">
                  <span className="font-mono">{i.reference}</span> · {i.debtor_name} ·
                  <span className="font-mono ml-1">{((i.total_ttc || 0) - (i.paid_amount || 0)).toFixed(2)} CHF</span>
                </Link>
              ))}
              {overdue.length > 3 && (
                <Link to="/factures" className="text-xs text-[#1F4E3D] hover:underline">+{overdue.length - 3} autres…</Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* À produire en priorité */}
      {productionNeeds.items.length > 0 && (
        <Card className="border-[#E5E5E0]" data-testid="production-needs-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="w-4 h-4 text-[#1F4E3D]" />
              <span className="text-sm font-medium text-[#1A1C1A]">À produire en priorité</span>
              <span className="text-[10px] text-[#A3A6A3]">
                Buffer {productionNeeds.buffer_percent}% · stock min {productionNeeds.min_stock_threshold}
              </span>
              <Link
                to={`/ordres?prefill=${encodeURIComponent(productionNeeds.items.map((i) => `${i.product_id}:${i.production_target}`).join(","))}`}
                className="ml-auto px-3 py-1.5 text-xs rounded-md bg-[#1F4E3D] text-white hover:bg-[#173B2E] flex items-center gap-1.5"
                data-testid="dashboard-create-of-link"
              >
                <Factory className="w-3.5 h-3.5" /> Créer un OF avec ces quantités
              </Link>
            </div>
            <div className="space-y-2">
              {productionNeeds.items.map((item) => (
                <div
                  key={item.product_id}
                  className={`flex items-center gap-3 p-3 rounded border ${item.is_critical ? "bg-[#FCEEEA] border-[#E8C1B4]" : "bg-[#FAFAF7] border-[#E5E5E0]"}`}
                  data-testid={`prod-need-${item.product_id}`}
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#1A1C1A]">{item.product_name}</div>
                    <div className="text-[11px] text-[#575A57]">
                      Demande {item.pending_demand} · Stock {item.current_stock}
                      {item.buffer_anticipation > 0 && ` · Buffer +${item.buffer_anticipation}`}
                      {item.pending_orders.length > 0 && ` · ${item.pending_orders.length} commande${item.pending_orders.length > 1 ? "s" : ""} en attente`}
                    </div>
                  </div>
                  {item.is_critical && (
                    <Badge className="bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Critique
                    </Badge>
                  )}
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wide text-[#A3A6A3]">À produire</div>
                    <div className="font-mono text-xl text-[#1F4E3D]">{item.production_target}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two columns: Alerts + Recent activity */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Alerts */}
        <div className="xl:col-span-2 bg-white border border-[#E5E5E0] rounded-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57]">Alertes</div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A] mt-1">Stock & Dates limites</h2>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="text-sm font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#C05C47]" /> Stock sous le seuil
              </div>
              {stats.low_stock.length === 0 ? (
                <div className="text-sm text-[#575A57] py-3 px-4 bg-[#F9F9F6] rounded">Aucune alerte stock.</div>
              ) : (
                <div className="space-y-2" data-testid="low-stock-list">
                  {stats.low_stock.map((item) => (
                    <div key={item.product.id} className="flex items-center justify-between py-2.5 px-4 bg-[#FCEEEA] border border-[#E8C1B4] rounded">
                      <div>
                        <div className="text-sm font-medium text-[#1A1C1A]">{item.product.name}</div>
                        <div className="text-xs font-mono text-[#575A57]">{item.product.sku}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono text-[#C05C47] font-semibold">{fmtNum(item.current_quantity, item.product.unit)}</div>
                        <div className="text-xs text-[#575A57]">Seuil: {fmtNum(item.product.min_stock, item.product.unit)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-sm font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#D4A373]" /> Lots expirant bientôt
              </div>
              {stats.expiring.length === 0 ? (
                <div className="text-sm text-[#575A57] py-3 px-4 bg-[#F9F9F6] rounded">Aucun lot à surveiller.</div>
              ) : (
                <div className="space-y-2" data-testid="expiring-list">
                  {stats.expiring.map((item) => (
                    <div key={item.lot.id} className="flex items-center justify-between py-2.5 px-4 bg-[#FBF4E9] border border-[#E8D3A8] rounded">
                      <div>
                        <div className="text-sm font-medium text-[#1A1C1A]">{item.product?.name || "Produit supprimé"}</div>
                        <div className="text-xs font-mono text-[#575A57]">Lot {item.lot.lot_number}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono text-[#1A1C1A]">{fmtDate(item.lot.dlc)}</div>
                        <div className="text-xs text-[#D4A373] font-medium">
                          {item.days_left < 0 ? `Périmé (${Math.abs(item.days_left)}j)` : `J-${item.days_left}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent movements */}
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
          <div className="mb-5">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57]">Activité</div>
            <h2 className="font-heading text-xl font-medium text-[#1A1C1A] mt-1">Mouvements récents</h2>
          </div>
          {stats.recent_movements.length === 0 ? (
            <div className="text-sm text-[#575A57] py-3 px-4 bg-[#F9F9F6] rounded">Aucun mouvement.</div>
          ) : (
            <div className="space-y-3" data-testid="recent-movements">
              {stats.recent_movements.map((m) => {
                // Phase 98af — Affiche le distributeur associé au mouvement
                // (destination pour IN, source pour OUT, from→to pour TRANSFERT)
                const distSrc = distributorName(m.from_distributor_id);
                const distDst = distributorName(m.to_distributor_id);
                const distMain = distributorName(m.distributor_id);
                let distLabel = null;
                if (distSrc && distDst) distLabel = `${distSrc} → ${distDst}`;
                else if (distDst) distLabel = `→ ${distDst}`;
                else if (distSrc) distLabel = `${distSrc} →`;
                else if (distMain) distLabel = distMain;
                return (
                  <div key={m.id} className="flex items-start gap-3 pb-3 border-b border-[#F0F0EB] last:border-0 last:pb-0" data-testid={`movement-row-${m.id}`}>
                    <div className="w-8 h-8 rounded-md bg-[#F4F4F0] flex items-center justify-center shrink-0 mt-0.5">
                      <MovementIcon type={m.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{movementLabel[m.type]}</Badge>
                        <span className="text-sm font-mono">{fmtNum(m.quantity, productUnit(m.product_id))}</span>
                      </div>
                      <div className="text-sm text-[#1A1C1A] mt-1 break-words" title={productName(m.product_id)} data-testid="movement-product-name">{productName(m.product_id)}</div>
                      {distLabel && (
                        <div className="text-xs text-[#1F4E3D] mt-0.5 break-words flex items-center gap-1" data-testid="movement-distributor-name">
                          <Building2 className="w-3 h-3 shrink-0" />
                          <span>{distLabel}</span>
                        </div>
                      )}
                      <div className="text-xs text-[#575A57] mt-0.5">{fmtDateTime(m.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
